import { inject, Injectable, OnDestroy } from '@angular/core'
import { AllocationRole, User } from 'src/app/models/user'
import { FeathersService } from '../feathers.service'
import { firstValueFrom, from, Observable, Subscription } from 'rxjs'
import { map, shareReplay } from 'rxjs/operators'
import { Workgroup } from '../../models/workgroup'
import { Assistant } from '../../models/assistant'
import { coerceToArray } from '../utils'
import { Contact } from '../../models/contact'
import { UserCredentials } from '../../models/UserCredentials'
import { Role } from 'src/app/models/Role'

@Injectable({
  providedIn: 'root',
})
export class UsersService implements OnDestroy {
  private feathers = inject(FeathersService)

  private allUsersWithContacts$: Observable<User[]>
  private allUsersWithContactsSub: Subscription

  private allUsers$: Observable<User[]>
  private allUsersSub: Subscription

  private allCoordinators$: Observable<User[]>
  private allCoordinatorsSub: Subscription
  private TAG = 'UsersService'

  async getCurrentUserPromise(): Promise<User> {
    return await this.feathers.getCurrentUserPromise()
  }

  getCurrentUser(): Observable<User> {
    return this.feathers.getCurrentUser().pipe(shareReplay({ bufferSize: 1, refCount: true }))
  }

  getCurrentUserWorkgroup(): Observable<Workgroup> {
    return this.getCurrentUser().pipe(map((user) => user.workgroup))
  }

  getUserWorkgroupByUserId(userId: number): Observable<Workgroup> {
    return this.getUserById(userId).pipe(map((user): Workgroup => user.workgroup))
  }

  getUserById(id: number) {
    return this.feathers.find(User, { id }).pipe(map((cc) => cc[0]))
  }

  async getUserByIdWithJoinedRoles(id: number) {
    return (
      await firstValueFrom(
        this.feathers.find(User, {
          id,
          $join: { assistant: true, client: true, worker: true },
        }),
      )
    )?.at(0)
  }

  public getAllUsersWithContacts() {
    return this.feathers.find(User, {
      $join: { contacts: true },
      role_system: false,
    })
  }

  public getAllUsers() {
    return this.feathers.find(User, {
      $join: { assistant: true, client: true, worker: true },
      role_system: false,
    })
  }

  public getAllWorkersAndAssistants$(): Observable<User[]> {
    return this.feathers.find(User, {
      $role: [AllocationRole.OaCoordinator, AllocationRole.Admin, AllocationRole.OaAssistant],
    })
  }

  public async update(u: User) {
    const patched = await this.feathers.patch<User>('users', u)
  }

  public patchPartial(u: Partial<User>) {
    // needs id
    return this.feathers.patchWithFactory(User, u)
  }

  public updateOrCreate(u: User) {
    return this.feathers.patchsert('users', u)
  }

  public async registerNewUser(u: User): Promise<User> {
    return await this.feathers.register(u)
  }

  public async registerNewUserAndAuthenticate(u: User): Promise<User> {
    return await this.feathers.registerAndAuthenticate(u)
  }

  getAllUsersWithContactsCached() {
    if (!this.allUsersWithContacts$) this.initAllUsersWithContacts$()
    return this.allUsersWithContacts$
  }

  getAllUsersCached() {
    if (!this.allUsers$) this.initAllUsers$()
    return this.allUsers$
  }

  initAllUsersWithContacts$() {
    this.allUsersWithContacts$ = this.getAllUsersWithContacts().pipe(shareReplay({ bufferSize: 1, refCount: true }))
    this.allUsersWithContactsSub = this.allUsersWithContacts$.subscribe()
  }

  initAllUsers$() {
    this.allUsers$ = this.getAllUsers().pipe(shareReplay({ bufferSize: 1, refCount: true }))
    this.allUsersSub = this.allUsers$.subscribe()
  }

  ngOnDestroy() {
    this.allUsersWithContactsSub?.unsubscribe()
    this.allUsersSub?.unsubscribe()
    this.allCoordinatorsSub?.unsubscribe()
  }

  getAllCoordinatorsWithCaching() {
    if (!this.allCoordinators$) this.initAllCoordinatorsObservable()
    return this.allCoordinators$.pipe(map((users) => users.filter(
      (user) => user.isRoleInAnyAllocation(AllocationRole.OaCoordinator)
    )))
  }

  getCurrentAllocationCoordinatorsWithCaching() {
    if (!this.allCoordinators$) this.initAllCoordinatorsObservable()
    return this.allCoordinators$.pipe(map((users) => users.filter((user) => user.isOaCoordinator())))
  }

  initAllCoordinatorsObservable() {
    this.allCoordinators$ = this.feathers.find(User, { $role: [AllocationRole.OaCoordinator] }).pipe(
      map((coordinators) => coordinators.sort(User.sortByName)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
    this.allCoordinatorsSub = this.allCoordinators$.subscribe()
  }

  public getUserByAssistantId(assistantId: number): Observable<User> {
    return this.feathers.find(Assistant, { id: assistantId }).pipe(
      map((a) => {
        return a[0].worker.user
      }),
    )
  }

  public isCurrentUserAdmin() {
    return this.getCurrentUser().pipe(map((user) => user.isAdmin()))
  }

  public isCurrentUserSuperadmin() {
    return this.getCurrentUser().pipe(map((user) => user.isSuperadmin()))
  }

  getUserByEmail(
    email: string,
    join: { client?: boolean; assistant?: boolean; worker?: boolean } = {},
  ): Observable<User> {
    return this.feathers.find(User, { email, $join: join }).pipe(map((u) => coerceToArray(u)[0]))
  }

  async getUsersPrimaryPhoneContact(userId: number) {
    const contacts = await this.feathers.getOne(Contact, {
      userId,
      contactType: 'telefon',
      primary: true,
    })
    if (contacts && contacts.length) {
      return contacts[0]
    }
  }

  getUserCredentials(userId: number, socialServiceId: number): Observable<UserCredentials> {
    return from(
      this.feathers.service(UserCredentials.serviceName).find({
        query: {
          userId,
          socialServiceId,
        },
      }),
    ).pipe(
      map((response) => {
        return response
      }),
    )
  }

  // Fetch all available roles from the /roles endpoint
  public getAllRoles(): Observable<Role[]> {
    return from(this.feathers.service('roles').find()).pipe(map((result: any) => result.data || result))
  }
}
